import type { VectorEdge, VectorNetwork, VectorVertex } from "@/document/model/vectorNetwork";
import { createId } from "@/lib/ids";

const EPSILON = 1e-9;

export interface ExtrudeEdgesOptions {
  dx: number;
  dy: number;
  /**
   * When true and multiple edges are selected, endpoints stay shared and each
   * vertex accumulates contributions from incident selected edges — producing a
   * mutual corner where edges meet.
   */
  mutual?: boolean;
  /**
   * When true, decompose the drag into the primary edge's tangent/normal frame
   * and replay those components in each edge's local frame.
   * Without `mutual`, each edge gets independent endpoint copies.
   * With `mutual`, contributions are accumulated onto shared endpoints.
   */
  relative?: boolean;
  /** Edge that defines the local frame for `relative` mode. */
  primaryEdgeId?: string;
  /** Optional id factory for deterministic previews / commits. */
  createId?: (prefix: string) => string;
}

export interface ExtrudeEdgesResult {
  network: VectorNetwork;
  /** New extruded edge copies (one per selected edge). */
  newEdgeIds: string[];
  /** Bridge edges from each original endpoint to its extruded copy. */
  connectorEdgeIds: string[];
  /** All newly created vertex ids. */
  newVertexIds: string[];
  /**
   * Original vertex id → extruded vertex id.
   * Only populated for shared-endpoint modes (not relative-without-mutual).
   */
  vertexMap: Record<string, string>;
}

/**
 * Snaps a drag delta to the nearest of horizontal, vertical, or any selected
 * edge's perpendicular axis.
 */
export function constrainExtrudeDragDelta(
  dx: number,
  dy: number,
  network: VectorNetwork,
  edgeIds: string[],
): { dx: number; dy: number } {
  const axes: Array<{ x: number; y: number }> = [
    { x: 1, y: 0 },
    { x: 0, y: 1 },
  ];
  const vertexById = new Map(network.vertices.map(vertex => [vertex.id, vertex] as const));
  for (const edge of uniqueExistingEdges(network, edgeIds)) {
    const from = vertexById.get(edge.from.vertexId);
    const to = vertexById.get(edge.to.vertexId);
    if (!from || !to) {
      continue;
    }
    const perp = unitPerpendicular(from, to);
    if (perp) {
      axes.push(perp);
    }
  }

  let bestAbs = -1;
  let best = { dx, dy };
  for (const axis of axes) {
    const amount = dx * axis.x + dy * axis.y;
    const score = Math.abs(amount);
    if (score > bestAbs) {
      bestAbs = score;
      best = { dx: axis.x * amount, dy: axis.y * amount };
    }
  }
  return best;
}

/**
 * Extrudes selected edges by copying them with an offset and connecting each
 * original endpoint to its copy. Original edges are preserved.
 */
export function extrudeEdges(
  network: VectorNetwork,
  edgeIds: string[],
  options: ExtrudeEdgesOptions,
): ExtrudeEdgesResult {
  const makeId = options.createId ?? createId;
  const selected = uniqueExistingEdges(network, edgeIds);
  if (selected.length === 0) {
    return emptyResult(network);
  }

  const vertexById = new Map(network.vertices.map(vertex => [vertex.id, vertex] as const));
  const delta = { x: options.dx, y: options.dy };
  const mutual = options.mutual === true && selected.length >= 2;
  const relative = options.relative === true;

  // Relative without mutual: each edge keeps private endpoint copies.
  if (relative && !mutual) {
    return extrudeRelativeIndependent(network, selected, vertexById, delta, {
      primaryEdgeId: options.primaryEdgeId,
      makeId,
    });
  }

  const endpointIds = uniqueEndpointIds(selected);
  const offsets = computeEndpointOffsets(selected, vertexById, delta, {
    mutual,
    relative,
    primaryEdgeId: options.primaryEdgeId,
  });

  const next = cloneNetwork(network);
  const vertexMap: Record<string, string> = {};
  const newVertexIds: string[] = [];

  for (const vertexId of endpointIds) {
    const original = vertexById.get(vertexId);
    if (!original) {
      continue;
    }
    const offset = offsets.get(vertexId) ?? { x: 0, y: 0 };
    const newId = makeId("vertex");
    vertexMap[vertexId] = newId;
    newVertexIds.push(newId);
    next.vertices.push({
      id: newId,
      x: original.x + offset.x,
      y: original.y + offset.y,
      handleMode: original.handleMode,
      strokeJoin: original.strokeJoin,
      cornerRadius: original.cornerRadius,
    });
  }

  const newEdgeIds: string[] = [];
  for (const edge of selected) {
    const fromId = vertexMap[edge.from.vertexId];
    const toId = vertexMap[edge.to.vertexId];
    if (!fromId || !toId) {
      continue;
    }
    const fromOffset = offsets.get(edge.from.vertexId) ?? { x: 0, y: 0 };
    const toOffset = offsets.get(edge.to.vertexId) ?? { x: 0, y: 0 };
    const edgeId = makeId("edge");
    newEdgeIds.push(edgeId);
    next.edges.push({
      id: edgeId,
      from: {
        vertexId: fromId,
        handle: offsetHandle(edge.from.handle, fromOffset),
      },
      to: {
        vertexId: toId,
        handle: offsetHandle(edge.to.handle, toOffset),
      },
      directed: edge.directed,
    });
  }

  const connectorEdgeIds: string[] = [];
  for (const vertexId of endpointIds) {
    const newId = vertexMap[vertexId];
    if (!newId || hasEdge(next, vertexId, newId)) {
      continue;
    }
    const edgeId = makeId("edge");
    connectorEdgeIds.push(edgeId);
    next.edges.push({
      id: edgeId,
      from: { vertexId },
      to: { vertexId: newId },
      directed: false,
    });
  }

  return {
    network: next,
    newEdgeIds,
    connectorEdgeIds,
    newVertexIds,
    vertexMap,
  };
}

function extrudeRelativeIndependent(
  network: VectorNetwork,
  selected: VectorEdge[],
  vertexById: Map<string, VectorVertex>,
  delta: { x: number; y: number },
  options: { primaryEdgeId?: string; makeId: (prefix: string) => string },
): ExtrudeEdgesResult {
  const next = cloneNetwork(network);
  const primaryEdge = selected.find(edge => edge.id === options.primaryEdgeId) ?? selected[0];
  if (!primaryEdge) {
    return emptyResult(network);
  }

  const centroid = selectionCentroid(selected, vertexById);
  const primaryFrame = outwardAlignedFrame(primaryEdge, vertexById, centroid);
  if (!primaryFrame) {
    return emptyResult(network);
  }

  // Express the cursor delta in the dragged edge's local tangent/normal frame,
  // then replay those components on every other edge in its own local frame.
  const tangentAmount = delta.x * primaryFrame.tangent.x + delta.y * primaryFrame.tangent.y;
  const normalAmount = delta.x * primaryFrame.normal.x + delta.y * primaryFrame.normal.y;

  const newEdgeIds: string[] = [];
  const connectorEdgeIds: string[] = [];
  const newVertexIds: string[] = [];

  for (const edge of selected) {
    const from = vertexById.get(edge.from.vertexId);
    const to = vertexById.get(edge.to.vertexId);
    if (!from || !to) {
      continue;
    }

    const offset = edge.id === primaryEdge.id
      ? { ...delta }
      : relativeOffsetInEdgeFrame(edge, vertexById, centroid, tangentAmount, normalAmount);

    const fromId = options.makeId("vertex");
    const toId = options.makeId("vertex");
    newVertexIds.push(fromId, toId);
    next.vertices.push(
      {
        id: fromId,
        x: from.x + offset.x,
        y: from.y + offset.y,
        handleMode: from.handleMode,
        strokeJoin: from.strokeJoin,
        cornerRadius: from.cornerRadius,
      },
      {
        id: toId,
        x: to.x + offset.x,
        y: to.y + offset.y,
        handleMode: to.handleMode,
        strokeJoin: to.strokeJoin,
        cornerRadius: to.cornerRadius,
      },
    );

    const edgeId = options.makeId("edge");
    newEdgeIds.push(edgeId);
    next.edges.push({
      id: edgeId,
      from: { vertexId: fromId, handle: offsetHandle(edge.from.handle, offset) },
      to: { vertexId: toId, handle: offsetHandle(edge.to.handle, offset) },
      directed: edge.directed,
    });

    for (const [originalId, newId] of [[edge.from.vertexId, fromId], [edge.to.vertexId, toId]] as const) {
      if (hasEdge(next, originalId, newId)) {
        continue;
      }
      const connectorId = options.makeId("edge");
      connectorEdgeIds.push(connectorId);
      next.edges.push({
        id: connectorId,
        from: { vertexId: originalId },
        to: { vertexId: newId },
        directed: false,
      });
    }
  }

  return {
    network: next,
    newEdgeIds,
    connectorEdgeIds,
    newVertexIds,
    vertexMap: {},
  };
}

function relativeOffsetInEdgeFrame(
  edge: VectorEdge,
  vertexById: Map<string, VectorVertex>,
  centroid: { x: number; y: number },
  tangentAmount: number,
  normalAmount: number,
): { x: number; y: number } {
  const frame = outwardAlignedFrame(edge, vertexById, centroid);
  if (!frame) {
    return { x: 0, y: 0 };
  }
  return {
    x: frame.tangent.x * tangentAmount + frame.normal.x * normalAmount,
    y: frame.tangent.y * tangentAmount + frame.normal.y * normalAmount,
  };
}

/** Unit tangent + normal with normal oriented away from the selection centroid. */
function outwardAlignedFrame(
  edge: VectorEdge,
  vertexById: Map<string, VectorVertex>,
  centroid: { x: number; y: number },
): { tangent: { x: number; y: number }; normal: { x: number; y: number } } | undefined {
  const from = vertexById.get(edge.from.vertexId);
  const to = vertexById.get(edge.to.vertexId);
  if (!from || !to) {
    return undefined;
  }
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len <= EPSILON) {
    return undefined;
  }
  let tangent = { x: dx / len, y: dy / len };
  // CCW perpendicular of tangent.
  let normal = { x: -tangent.y, y: tangent.x };
  const mid = { x: (from.x + to.x) / 2, y: (from.y + to.y) / 2 };
  const outward = { x: mid.x - centroid.x, y: mid.y - centroid.y };
  if (Math.hypot(outward.x, outward.y) > EPSILON && outward.x * normal.x + outward.y * normal.y < 0) {
    tangent = { x: -tangent.x, y: -tangent.y };
    normal = { x: -normal.x, y: -normal.y };
  }
  return { tangent, normal };
}

function selectionCentroid(
  edges: VectorEdge[],
  vertexById: Map<string, VectorVertex>,
): { x: number; y: number } {
  let x = 0;
  let y = 0;
  let count = 0;
  for (const edge of edges) {
    const from = vertexById.get(edge.from.vertexId);
    const to = vertexById.get(edge.to.vertexId);
    if (!from || !to) {
      continue;
    }
    x += (from.x + to.x) / 2;
    y += (from.y + to.y) / 2;
    count += 1;
  }
  if (count === 0) {
    return { x: 0, y: 0 };
  }
  return { x: x / count, y: y / count };
}

function computeEndpointOffsets(
  edges: VectorEdge[],
  vertexById: Map<string, VectorVertex>,
  delta: { x: number; y: number },
  options: { mutual: boolean; relative: boolean; primaryEdgeId?: string },
): Map<string, { x: number; y: number }> {
  const offsets = new Map<string, { x: number; y: number }>();

  if (options.mutual && options.relative) {
    const primaryEdge = edges.find(edge => edge.id === options.primaryEdgeId) ?? edges[0];
    if (!primaryEdge) {
      return offsets;
    }
    const centroid = selectionCentroid(edges, vertexById);
    const primaryFrame = outwardAlignedFrame(primaryEdge, vertexById, centroid);
    if (!primaryFrame) {
      return offsets;
    }
    const tangentAmount = delta.x * primaryFrame.tangent.x + delta.y * primaryFrame.tangent.y;
    const normalAmount = delta.x * primaryFrame.normal.x + delta.y * primaryFrame.normal.y;
    for (const edge of edges) {
      // Primary reconstructs to `delta`; others get the same local-frame replay.
      const contrib = edge.id === primaryEdge.id
        ? { ...delta }
        : relativeOffsetInEdgeFrame(edge, vertexById, centroid, tangentAmount, normalAmount);
      accumulateOffset(offsets, edge.from.vertexId, contrib);
      accumulateOffset(offsets, edge.to.vertexId, contrib);
    }
    return offsets;
  }

  if (options.mutual) {
    for (const edge of edges) {
      const contrib = edgePerpendicularContribution(edge, vertexById, delta);
      accumulateOffset(offsets, edge.from.vertexId, contrib);
      accumulateOffset(offsets, edge.to.vertexId, contrib);
    }
    return offsets;
  }

  for (const edge of edges) {
    offsets.set(edge.from.vertexId, { ...delta });
    offsets.set(edge.to.vertexId, { ...delta });
  }
  return offsets;
}

function edgePerpendicularContribution(
  edge: VectorEdge,
  vertexById: Map<string, VectorVertex>,
  delta: { x: number; y: number },
): { x: number; y: number } {
  const from = vertexById.get(edge.from.vertexId);
  const to = vertexById.get(edge.to.vertexId);
  if (!from || !to) {
    return { x: 0, y: 0 };
  }
  const perp = unitPerpendicular(from, to);
  if (!perp) {
    return { x: 0, y: 0 };
  }
  const amount = delta.x * perp.x + delta.y * perp.y;
  return { x: perp.x * amount, y: perp.y * amount };
}

/**
 * Adds a contribution, merging with an existing offset when the new contribution
 * is parallel (avoids double-counting collinear incident edges in mutual mode).
 */
function accumulateOffset(
  offsets: Map<string, { x: number; y: number }>,
  vertexId: string,
  contrib: { x: number; y: number },
): void {
  if (Math.hypot(contrib.x, contrib.y) <= EPSILON) {
    return;
  }
  const current = offsets.get(vertexId);
  if (!current) {
    offsets.set(vertexId, { ...contrib });
    return;
  }
  if (Math.hypot(current.x, current.y) <= EPSILON) {
    offsets.set(vertexId, { ...contrib });
    return;
  }
  // Parallel (or anti-parallel): keep the larger magnitude along that axis.
  const cross = current.x * contrib.y - current.y * contrib.x;
  if (Math.abs(cross) <= EPSILON) {
    const currentLen = Math.hypot(current.x, current.y);
    const contribLen = Math.hypot(contrib.x, contrib.y);
    const sameDirection = current.x * contrib.x + current.y * contrib.y >= 0;
    if (contribLen > currentLen) {
      offsets.set(vertexId, sameDirection ? { ...contrib } : { x: -contrib.x, y: -contrib.y });
    }
    return;
  }
  offsets.set(vertexId, { x: current.x + contrib.x, y: current.y + contrib.y });
}

function unitPerpendicular(
  from: { x: number; y: number },
  to: { x: number; y: number },
): { x: number; y: number } | undefined {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const len = Math.hypot(dx, dy);
  if (len <= EPSILON) {
    return undefined;
  }
  return { x: -dy / len, y: dx / len };
}

function offsetHandle(
  handle: { x: number; y: number } | undefined,
  offset: { x: number; y: number },
): { x: number; y: number } | undefined {
  if (!handle) {
    return undefined;
  }
  return { x: handle.x + offset.x, y: handle.y + offset.y };
}

function uniqueEndpointIds(edges: VectorEdge[]): string[] {
  const seen = new Set<string>();
  const ids: string[] = [];
  for (const edge of edges) {
    for (const vertexId of [edge.from.vertexId, edge.to.vertexId]) {
      if (seen.has(vertexId)) {
        continue;
      }
      seen.add(vertexId);
      ids.push(vertexId);
    }
  }
  return ids;
}

function uniqueExistingEdges(network: VectorNetwork, edgeIds: string[]): VectorEdge[] {
  const seen = new Set<string>();
  const edges: VectorEdge[] = [];
  for (const edgeId of edgeIds) {
    if (seen.has(edgeId)) {
      continue;
    }
    const edge = network.edges.find(candidate => candidate.id === edgeId);
    if (!edge) {
      continue;
    }
    seen.add(edgeId);
    edges.push(edge);
  }
  return edges;
}

function cloneNetwork(network: VectorNetwork): VectorNetwork {
  return {
    fillRule: network.fillRule,
    fillOverrideTable: network.fillOverrideTable ? structuredClone(network.fillOverrideTable) : undefined,
    regions: network.regions.map(region => ({ ...region, edgeIds: [...region.edgeIds] })),
    vertices: network.vertices.map(vertex => ({ ...vertex })),
    edges: network.edges.map(edge => ({
      ...edge,
      from: { vertexId: edge.from.vertexId, handle: edge.from.handle ? { ...edge.from.handle } : undefined },
      to: { vertexId: edge.to.vertexId, handle: edge.to.handle ? { ...edge.to.handle } : undefined },
    })),
  };
}

function hasEdge(network: VectorNetwork, fromVertexId: string, toVertexId: string): boolean {
  return network.edges.some(edge =>
    (edge.from.vertexId === fromVertexId && edge.to.vertexId === toVertexId)
    || (edge.from.vertexId === toVertexId && edge.to.vertexId === fromVertexId),
  );
}

function emptyResult(network: VectorNetwork): ExtrudeEdgesResult {
  return {
    network: cloneNetwork(network),
    newEdgeIds: [],
    connectorEdgeIds: [],
    newVertexIds: [],
    vertexMap: {},
  };
}
